'use client';

import { useState, useRef } from 'react';
import { Transition } from '@headlessui/react';
import Image from 'next/image';
import DesktopImage from '@/public/images/previews-home-desktop.png';
import PhoneImage from '@/public/images/previews-home-phone.png';
import TabletImage from '@/public/images/previews-home-tablet.png';

export default function FeaturesHome02() {
  const [tab, setTab] = useState<number>(1);

  const tabs = useRef<HTMLDivElement>(null);

  return (
    <section>
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="py-12 md:py-20 border-t border-slate-200">
          {/* Section header */}
          <div className="max-w-3xl mx-auto text-center pb-12 md:pb-20">
            <h2 className="h2 font-merriweather text-zinc-800 max-w-[520px] mx-auto">
              With you at all times, home or away
            </h2>
          </div>

          {/* Section content */}
          <div className="max-w-xl mx-auto md:max-w-none flex flex-col md:flex-row md:items-start md:space-x-8 lg:space-x-16 xl:space-x-18 space-y-8 space-y-reverse md:space-y-0">
            {/* Tabs items (images) */}
            <div className="md:rtl md:w-5/12 lg:w-1/2 order-1 md:order-none relative">
              <div className="relative min-h-[575px]" data-aos="fade-down" ref={tabs}>
                {/* Item 1 */}
                <Transition
                  show={tab === 1}
                  appear={true}
                  enter="transition ease-in-out duration-700 delay-300 absolute transform"
                  enterFrom="opacity-0 -translate-y-16"
                  enterTo="opacity-100 translate-y-0"
                  leave="transition ease-in-out duration-300 transform absolute inset-0"
                  leaveFrom="opacity-100 translate-y-0"
                  leaveTo="opacity-0 translate-y-16"
                  unmount={false}
                >
                  <div className="absolute inline-flex flex-col">
                    <Image
                      className="md:max-w-none mx-auto rounded"
                      src={DesktopImage}
                      width={541}
                      height={575}
                      alt="Desktop"
                    />
                  </div>
                </Transition>
                {/* Item 2 */}
                <Transition
                  show={tab === 2}
                  appear={true}
                  enter="transition ease-in-out duration-700 delay-300 absolute transform"
                  enterFrom="opacity-0 -translate-y-16"
                  enterTo="opacity-100 translate-y-0"
                  leave="transition ease-in-out duration-300 transform absolute inset-0"
                  leaveFrom="opacity-100 translate-y-0"
                  leaveTo="opacity-0 translate-y-16"
                  unmount={false}
                >
                  <div className="absolute inline-flex flex-col">
                    <Image
                      className="md:max-w-none mx-auto rounded"
                      src={PhoneImage}
                      width={541}
                      height={575}
                      alt="Phone"
                    />
                  </div>
                </Transition>
                {/* Item 3 */}
                <Transition
                  show={tab === 3}
                  appear={true}
                  enter="transition ease-in-out duration-700 delay-300 absolute transform"
                  enterFrom="opacity-0 -translate-y-16"
                  enterTo="opacity-100 translate-y-0"
                  leave="transition ease-in-out duration-300 transform absolute inset-0"
                  leaveFrom="opacity-100 translate-y-0"
                  leaveTo="opacity-0 translate-y-16"
                  unmount={false}
                >
                  <div className="absolute inline-flex flex-col">
                    <Image
                      className="md:max-w-none mx-auto rounded"
                      src={TabletImage}
                      width={541}
                      height={575}
                      alt="Tablet"
                    />
                  </div>
                </Transition>
              </div>
            </div>

            {/* Content */}
            <div className="md:w-7/12 lg:w-1/2" data-aos="fade-up">
              <div className="mb-8 text-center md:text-left">
                {/* <h3 className="h3 text-zinc-800 font-merriweather mb-3">Built exclusively for you</h3> */}
                <p className="text-xl text-zinc-800">
                  Unlike other platforms, Penzu works well on every device. You can write in any web
                  browser or use our custom built apps for iOS and Android.
                </p>
              </div>
              {/* Tabs buttons */}
              <div className="mb-8 md:mb-0">
                <button
                  className={`flex items-start text-left bg-white border-2 px-5 py-3 rounded shadow-md transition duration-300 ease-in-out mb-3 ${
                    tab !== 1
                      ? 'border-transparent opacity-50 hover:opacity-75'
                      : 'border-2 border-blue-500 opacity-100'
                  }`}
                  onClick={(e) => {
                    e.preventDefault();
                    setTab(1);
                  }}
                >
                  <svg
                    className="w-[23px] h-[21px] shrink-0 mt-1 mr-4"
                    width="23"
                    height="21"
                    viewBox="0 0 23 21"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect width="23" height="21" fill="white" />
                    <rect
                      x="1.75"
                      y="0.75"
                      width="19.5"
                      height="13.5"
                      rx="1.25"
                      fill="#F1F5F9"
                      stroke="#2174EA"
                      strokeWidth="1.5"
                    />
                    <rect
                      x="0.75"
                      y="-0.75"
                      width="5.5"
                      height="4.5"
                      transform="matrix(1 0 0 -1 8 18.5)"
                      fill="#F1F5F9"
                      stroke="#2174EA"
                      strokeWidth="1.5"
                    />
                  </svg>
                  <div>
                    <div className="text-zinc-800 font-medium mb-1">Desktop</div>
                    <div className="text-slate-500">
                      Perfect for long-form writing, Penzu shines on a computer or laptop where you
                      can enjoy all that Penzu has to offer.
                    </div>
                  </div>
                </button>
                <button
                  className={`flex items-start text-left bg-white border-2 px-5 py-3 rounded shadow-md transition duration-300 ease-in-out mb-3 ${
                    tab !== 2
                      ? 'border-transparent opacity-50 hover:opacity-75'
                      : 'border-2 border-blue-500 opacity-100'
                  }`}
                  onClick={(e) => {
                    e.preventDefault();
                    setTab(2);
                  }}
                >
                  <svg
                    className="w-[23px] h-[21px] shrink-0 mt-1 mr-4"
                    width="23"
                    height="21"
                    viewBox="0 0 23 21"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect width="23" height="21" fill="white" />
                    <rect
                      x="4.75"
                      y="0.75"
                      width="13.5"
                      height="19.5"
                      rx="1.25"
                      fill="#F1F5F9"
                      stroke="#2174EA"
                      strokeWidth="1.5"
                    />
                    <rect x="8" y="2" width="7" height="2" rx="1" fill="#2174EA" />
                  </svg>
                  <div>
                    <div className="text-zinc-800 font-medium mb-1">Phone</div>
                    <div className="text-slate-500">
                      When not near your computer, you can continue to write in and read your
                      journal entries.
                    </div>
                  </div>
                </button>
                <button
                  className={`flex items-start text-left bg-white border-2 px-5 py-3 rounded shadow-md transition duration-300 ease-in-out mb-3 ${
                    tab !== 3
                      ? 'border-transparent opacity-50 hover:opacity-75'
                      : 'border-2 border-blue-500 opacity-100'
                  }`}
                  onClick={(e) => {
                    e.preventDefault();
                    setTab(3);
                  }}
                >
                  <svg
                    className="w-[23px] h-[21px] shrink-0 mt-1 mr-4"
                    width="23"
                    height="21"
                    viewBox="0 0 23 21"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <g clipPath="url(#clip0_91_47)">
                      <rect width="23" height="21" fill="white" />
                      <rect
                        x="0.75"
                        y="0.75"
                        width="21.5"
                        height="19.5"
                        rx="1.25"
                        fill="#F1F5F9"
                        stroke="#2174EA"
                        strokeWidth="1.5"
                      />
                      <rect x="8" y="1" width="7" height="2" rx="1" fill="#2174EA" />
                    </g>
                    <defs>
                      <clipPath id="clip0_91_47">
                        <rect width="23" height="21" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>
                  <div>
                    <div className="text-zinc-800 font-medium mb-1">Tablet</div>
                    <div className="text-slate-500">
                      If you have an iPad or Android tablet you can enjoy the same familiar mobile
                      experience.
                    </div>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
